import { OrderState, OrderStatusGroup } from '@tcr-erp/shared';
import type { TenantOrder } from '@/mocks/orders';

const DEFAULT_DELIVERY_WINDOW_MS = 48 * 60 * 60 * 1000;
const APPROACHING_DEADLINE_WINDOW_MS = 24 * 60 * 60 * 1000;

export type DeliveryDeadlineSource = 'order' | 'default_48h';

export interface DeliveryOverdueStatus {
  deadline: Date | null;
  overdue: boolean;
  approachingDeadline: boolean;
  remainingMinutes: number | null;
  source: DeliveryDeadlineSource | null;
}

export interface DeliveryWarningBadge {
  color: 'red' | 'gold';
  label: string;
}

export interface DeliveryOverdueOrder {
  state: TenantOrder['state'];
  status_group?: TenantOrder['status_group'];
  created_at: string;
  confirmed_at?: string;
  completed_at?: string;
  delivery_days?: number;
  delivery_date?: string;
  shipping_state?: string;
  state_history?: TenantOrder['state_history'];
  shipping?: Pick<NonNullable<TenantOrder['shipping']>, 'estimated_days' | 'estimated_delivery' | 'state'>;
}

function validTimestamp(value: string | undefined): number | null {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function resolveConfirmedAt(order: DeliveryOverdueOrder): number | null {
  const confirmedAt = validTimestamp(order.confirmed_at);
  if (confirmedAt !== null) return confirmedAt;

  const confirmedHistory = order.state_history
    ?.filter((entry) => entry.state === OrderState.CONFIRMED)
    .map((entry) => validTimestamp(entry.changed_at))
    .filter((timestamp): timestamp is number => timestamp !== null)
    .sort((left, right) => left - right);

  return confirmedHistory?.[0] ?? null;
}

function resolveConfiguredDeadline(
  order: DeliveryOverdueOrder,
): number | null {
  const estimatedDays = order.shipping?.estimated_days ?? order.delivery_days;
  const createdAt = validTimestamp(order.created_at);
  if (
    typeof estimatedDays === 'number' &&
    Number.isFinite(estimatedDays) &&
    estimatedDays > 0 &&
    createdAt !== null
  ) {
    return createdAt + estimatedDays * 24 * 60 * 60 * 1000;
  }

  const explicitDeadline =
    validTimestamp(order.shipping?.estimated_delivery?.to) ??
    validTimestamp(order.shipping?.estimated_delivery?.from) ??
    validTimestamp(order.delivery_date);
  if (explicitDeadline !== null) return explicitDeadline;

  return null;
}

/**
 * Cảnh báo giao hàng trễ trên màn chi tiết đơn.
 *
 * - Có số ngày giao trên đơn: tính từ lúc tạo đơn.
 * - Có ngày giao cụ thể nhưng không có số ngày: dùng ngày đó.
 * - Không có hạn giao: mặc định 48 giờ kể từ lúc duyệt đơn.
 * - Đơn đã hoàn thành: so thời điểm hoàn thành với hạn để không biến một đơn
 *   giao đúng hạn thành quá hạn chỉ vì người dùng mở lại nhiều ngày sau.
 */
export function getDeliveryOverdueStatus(
  order: DeliveryOverdueOrder,
  now: Date = new Date(),
): DeliveryOverdueStatus {
  const confirmedAt = resolveConfirmedAt(order);
  const configuredDeadline = resolveConfiguredDeadline(order);
  const deadlineTimestamp = configuredDeadline ?? (
    confirmedAt === null ? null : confirmedAt + DEFAULT_DELIVERY_WINDOW_MS
  );
  const source: DeliveryDeadlineSource | null = configuredDeadline !== null
    ? 'order'
    : deadlineTimestamp !== null
      ? 'default_48h'
      : null;

  if (deadlineTimestamp === null) {
    return {
      deadline: null,
      overdue: false,
      approachingDeadline: false,
      remainingMinutes: null,
      source,
    };
  }

  if (
    order.state === OrderState.CANCELED ||
    order.state === OrderState.RETURNED ||
    order.status_group === OrderStatusGroup.CANCELLED
  ) {
    return {
      deadline: new Date(deadlineTimestamp),
      overdue: false,
      approachingDeadline: false,
      remainingMinutes: null,
      source,
    };
  }

  const delivered =
    order.state === OrderState.COMPLETED ||
    order.shipping?.state === 'DELIVERED' ||
    order.shipping_state === 'DELIVERED';
  const comparisonTimestamp = delivered
    ? validTimestamp(order.completed_at)
    : now.getTime();
  const overdue = comparisonTimestamp !== null && comparisonTimestamp > deadlineTimestamp;
  const remainingMs = deadlineTimestamp - now.getTime();
  const approachingDeadline = !delivered
    && !overdue
    && remainingMs > 0
    && remainingMs <= APPROACHING_DEADLINE_WINDOW_MS;

  return {
    deadline: new Date(deadlineTimestamp),
    overdue,
    approachingDeadline,
    remainingMinutes: approachingDeadline
      ? Math.ceil(remainingMs / (60 * 1000))
      : null,
    source,
  };
}

export function getDeliveryWarningBadge(
  status: Pick<DeliveryOverdueStatus, 'overdue' | 'approachingDeadline' | 'remainingMinutes'>,
): DeliveryWarningBadge | null {
  if (status.overdue) {
    return { color: 'red', label: 'Quá hạn giao hàng' };
  }
  if (status.approachingDeadline && status.remainingMinutes !== null) {
    const hours = Math.floor(status.remainingMinutes / 60);
    const minutes = status.remainingMinutes % 60;
    const remaining = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    return {
      color: 'gold',
      label: `Sắp quá hạn · còn ${remaining}`,
    };
  }
  return null;
}
